import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";

import Plus from "@assets/icons/rounded/add.svg";
import Search from "@assets/icons/rounded/search.svg";
import { useDebounce } from "@app/util/use-debounce";
import { ClickableComponent, TextFieldComponent } from "@app/core";
import { SelectComponent } from "@app/core/select";
import { getTranslatedPlatforms } from "@app/constants/platform";
import { getTranslatedStatusses } from "@app/constants/status";
import { ROUTES } from "@app/constants/routes";
import { useUrlListState } from "@app/util/use-url-list-state";

import styles from "./product-filter-bar-component.module.scss";

const ProductFilterBar = () => {
  const intl = useIntl();
  const { filters, setFilters } = useUrlListState(["search", "status", "platform"] as const);

  const currentStatus = Number(filters.status) || 0;
  const currentPlatform = Number(filters.platform) || 0;
  const [search, setSearch] = useState<string>(filters.search || "");
  const debouncedSearchTerm = useDebounce(search, 500);

  const translatedPlatforms = getTranslatedPlatforms(intl);
  const translatedStatusses = getTranslatedStatusses(intl);

  // Only push to the URL when the (debounced) search actually changed — never on mount, so a
  // restored `?page=N` survives.
  useEffect(() => {
    if (debouncedSearchTerm !== (filters.search || "")) {
      setFilters({ search: debouncedSearchTerm || undefined });
    }
  }, [debouncedSearchTerm]);

  const onPlatformChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setFilters({ platform: Number(e.target.value) || undefined });

  const onStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setFilters({ status: Number(e.target.value) || undefined });

  return (
    <div className={styles.filterBar}>
      <div className={styles.searchField}>
        <TextFieldComponent
          height={40}
          placeholder={intl.formatMessage({ id: "products.overview.filters.search.placeholder" })}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          type="text"
          id="search"
          icon={Search}
          width={318}
        />
      </div>

      <SelectComponent
        onChange={onStatusChange}
        value={currentStatus}
        options={[
          { id: 0, name: intl.formatMessage({ id: "general.select.status.placeholder" }) },
          ...translatedStatusses
        ]}
        autoWidth
      />
      <SelectComponent
        onChange={onPlatformChange}
        value={currentPlatform}
        options={[
          { id: 0, name: intl.formatMessage({ id: "general.select.platforms.placeholder" }) },
          ...translatedPlatforms
        ]}
        autoWidth
      />
      <ClickableComponent
        title={intl.formatMessage({ id: "products.overview.button.add" })}
        height={40}
        icon={Plus}
        iconSize="20px"
        iconFillColor="#ffffff"
        iconPosition="left"
        href={ROUTES.PRODUCTS_NEW}
      />
    </div>
  );
};

export { ProductFilterBar };
